import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {useEffect, useMemo, useState} from 'react';
import {GridColDef, GridSortModel, GridSortDirection} from '@mui/x-data-grid';
import AddDeliveryModal from 'src/components/Modals/AddDelivery';
import SearchApartments from 'src/components/SearchApartment';
// import {Apartment} from 'src/apis/autoGenerate';
// import {Apartment, Delivery, DeliveryUpdateReq} from 'src/apis/autoGenerate';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Delivery, DeliveryUpdateReq} from 'src/apis/autoGenerate/types';
import {
  deliveryServiceDelete,
  deliveryServiceFind,
  deliveryServiceUpdate,
} from 'src/apis/autoGenerate/services';
// import {deliveryServiceDelete, deliveryServiceFind, deliveryServiceUpdate} from 'src/apis';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import message from 'src/components/Message';
import SearchInput from 'src/components/SearchInput';
import {formatDate} from 'src/utils/common';
import {useOrgId, useCanEdit} from 'src/hooks';
import {useAppSelector} from 'src/redux/hooks';
import {selectProperty} from 'src/redux/slice/propertySlice';
import {PageHeader, EntioDataGrid} from 'src/components/ui';

export default function DeliveryPage() {
  const [AddDeliveryModalOpen, setAddDeliveryModalOpen] = useState(false);
  const [searchApartment, setSearchApartment] = useState<Apartment | null>();
  const [searchDelivery, setSearchDelivery] = useState<string>('');
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery>();
  const [deliveryActionIdAnchorEl, setDeliveryActionIdAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const deliveryActionOpen = Boolean(deliveryActionIdAnchorEl);
  const deliveryActionId = deliveryActionOpen ? 'tenant-action-popover' : undefined;
  const {orgId} = useOrgId();
  const property = useAppSelector(selectProperty);
  const canEdit = useCanEdit();
  useEffect(() => {
    if (orgId) {
      getList();
    }
  }, [orgId]); // Re-run when orgId changes
  // Fix the type definition for sortModel
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'createdAt',
      sort: 'desc' as GridSortDirection,
    },
  ]);

  useEffect(() => {
    getList();
  }, []);

  useEffect(() => {
    if (!AddDeliveryModalOpen) {
      setSelectedDelivery(undefined);
    }
  }, [AddDeliveryModalOpen]);

  const handleActionClose = () => {
    setDeliveryActionIdAnchorEl(null);
  };

  const handleSearchDeliveryByApartment = (val: Apartment | null) => {
    setSearchApartment(val ?? undefined);
    getList(searchDelivery, val?.id);
  };

  const handleSearchDeliveryByInfo = (info = '') => {
    setSearchDelivery(info);
    getList(info, searchApartment?.id);
  };

  const getList = async (name?: string, apartmentId?: string) => {
    if (!orgId) {
      console.error('orgId is not defined');
      return;
    }
    setLoading(true);

    try {
      // If specific apartment is selected, use it. Otherwise, use all accessible apartments
      let apartmentIds: string[];
      if (apartmentId) {
        apartmentIds = [apartmentId];
      } else {
        // Get all accessible apartment IDs from property list
        apartmentIds = property.apartmentList.map(apt => apt.id).filter(Boolean) as string[];

        if (apartmentIds.length === 0) {
          setDeliveries([]);
          return;
        }
      }

      const res = await deliveryServiceFind(orgId, undefined, apartmentIds, name);

      // Ensure response structure is correct before accessing properties
      if (!res || !res.data || !Array.isArray(res.data.deliveries)) {
        setDeliveries([]); // Prevents `undefined` state issues
        return;
      }

      const _deliveries = res.data.deliveries;

      setDeliveries(_deliveries.length ? _deliveries : []); // Ensure it's always an array
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      setDeliveries([]); // Ensure UI remains stable even on failure
    } finally {
      setLoading(false);
    }
  };

  const handleDeliveryActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    delivery: Delivery
  ) => {
    setSelectedDelivery(delivery);
    setDeliveryActionIdAnchorEl(event.currentTarget);
  };

  const handleDeliveryEdit = () => {
    setDeliveryActionIdAnchorEl(null);
    setAddDeliveryModalOpen(true);
  };

  const handleRemoveDelivery = () => {
    setLoading(true);
    deliveryServiceDelete(selectedDelivery?.id as string).then(() => {
      message.success('Remove Success');
      getList(searchDelivery, searchApartment?.id);
    });
  };

  const handleConfirm = async (updatedDelivery?: any) => {
    // For new deliveries, selectedDelivery will be undefined
    if (!selectedDelivery?.id) {
      // This is a new delivery, not an update
      setAddDeliveryModalOpen(false);
      getList();
      return;
    }

    if (!selectedDelivery.orgId) {
      console.error('🚨 Missing orgId in selectedDelivery!');
      message.error('Organization ID is missing. Please check your selection.');
      return;
    }

    setLoading(true);
    try {
      const updatedData: DeliveryUpdateReq = {
        delivery: {
          apartmentId: updatedDelivery?.apartmentId ?? selectedDelivery.apartmentId ?? '',
          description: updatedDelivery?.description ?? selectedDelivery.description ?? '',
          name: updatedDelivery?.name ?? selectedDelivery.name,
          orgId: selectedDelivery.orgId ?? '',
          pin: updatedDelivery?.pin ?? selectedDelivery.pin,
          validDateFrom: updatedDelivery?.validDateFrom ?? selectedDelivery.validDateFrom,
          validDateTo: updatedDelivery?.validDateTo ?? selectedDelivery.validDateTo,
        },
      };

      await deliveryServiceUpdate(selectedDelivery.id, updatedData);

      message.success('Updated successfully!');
      setAddDeliveryModalOpen(false);
      getList();
    } catch (error) {
      message.error('Update failed, please try again.');
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Delivery Name',
        width: 130,
        flex: 1,
      },
      {
        field: 'apartment',
        headerName: 'Apartment',
        width: 130,
        flex: 1,
        renderCell: (val: any) => {
          return <div>{val.value.name}</div>;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Valid Date',
        width: 130,
        flex: 2,
        renderCell: (val: any) => {
          return (
            <div>
              {formatDate(val.row.validDateFrom)} - {formatDate(val.row.validDateTo)}
            </div>
          );
        },
      },
      ...(canEdit
        ? [
            {
              field: 'id',
              headerName: '',
              width: 60,
              renderCell: (val: any) => {
                return (
                  <IconButton
                    onClick={e => {
                      handleDeliveryActionClick(e, val.row);
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ],
    [canEdit]
  );

  return (
    <Box>
      <PageHeader
        title="Deliveries"
        actions={
          canEdit ? (
            <Button
              variant="contained"
              onClick={() => {
                setSelectedDelivery(undefined);
                setAddDeliveryModalOpen(true);
              }}
              startIcon={<AddIcon />}
            >
              Delivery Authorization
            </Button>
          ) : undefined
        }
      />
      <Box sx={{display: 'flex', gap: 1.5, mb: 2}}>
        <SearchInput placeholder="Search Delivery" onSearch={handleSearchDeliveryByInfo} />
        <SearchApartments onChange={handleSearchDeliveryByApartment} />
      </Box>
      <Box mt={2}>
        <EntioDataGrid
          loading={loading}
          rows={deliveries}
          columns={columns}
          pageSizeOptions={[5, 10]}
          sortModel={sortModel}
          onSortModelChange={(newModel: GridSortModel) => setSortModel(newModel)}
          emptyMessage="No deliveries"
          emptyDescription="Create delivery authorizations to manage package access."
        />
      </Box>
      {AddDeliveryModalOpen && (
        <AddDeliveryModal
          open={AddDeliveryModalOpen}
          initData={selectedDelivery}
          handleCancel={() => {
            setAddDeliveryModalOpen(false);
          }}
          handleConfirm={handleConfirm}
        />
      )}
      <Popover
        id={deliveryActionId}
        open={deliveryActionOpen}
        anchorEl={deliveryActionIdAnchorEl}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          {canEdit && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={handleDeliveryEdit}>
                  <ListItemText primary="Edit" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setDeliveryActionIdAnchorEl(null);
                    handleRemoveDelivery();
                  }}
                >
                  <ListItemText primary="Remove" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Popover>
    </Box>
  );
}
